import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Play, Plus, Share2, MapPin, Instagram, Twitter, Globe, Music, Video, Calendar, Trophy, Users, TrendingUp, Mic2, Clock, Eye, MessageCircle } from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { TierBadge, VerifiedDMVBadge } from '../components/TierBadge';
import { StageEmptyState } from '../components/StageEmptyState';
import { TopFansCard, MyFanStatus } from '../components/TopFan';
import { CreatorChartCard } from '../components/CreatorChartCard';
import { ArtistSubscriptionSection } from '../components/ArtistSubscription';
import { ArtistPostsFeed } from '../components/ArtistPostsFeed';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { getProfileUrl } from '../utils/profileUtils';



export default function ArtistPage() {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const { playTrack } = usePlayer();
  const [artist, setArtist] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [videos, setVideos] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [chartBadges, setChartBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);

  useEffect(() => {
    fetchArtistData();
  }, [artistId]);

  const fetchArtistData = async () => {
    try {
      const [artistRes, tracksRes, videosRes, chartsRes, interviewsRes] = await Promise.all([
        fetch(`${API}/artists/${artistId}`),
        fetch(`${API}/tracks?artist_id=${artistId}`),
        fetch(`${API}/videos?artist_id=${artistId}`),
        fetch(`${API}/charts/top-20-artists`),
        fetch(`${API}/interviews?guest_id=${artistId}`)
      ]);

      if (artistRes.ok) {
        const artistData = await artistRes.json();
        setArtist(artistData);
        
        // CANONICAL PROFILE REDIRECT: If artist has a handle, redirect to /u/:handle
        // This ensures all profile views use the unified profile page
        if (artistData.handle) {
          console.log('[ArtistPage] Redirecting to canonical profile:', getProfileUrl(artistData));
          navigate(getProfileUrl(artistData), { replace: true });
          return;
        }
        // If no handle but has user_id, redirect to /u/id/:userId
        if (artistData.user_id) {
          console.log('[ArtistPage] Redirecting to user profile by ID:', artistData.user_id);
          navigate(`/u/id/${artistData.user_id}`, { replace: true });
          return;
        }
      } else if (artistRes.status === 404) {
        // Artist not found - try to resolve via user endpoint
        navigate('/user-not-found', { replace: true });
        return;
      }
      if (tracksRes.ok) {
        const tracksData = await tracksRes.json();
        setTracks(tracksData);
      }
      if (videosRes.ok) {
        const videosData = await videosRes.json();
        setVideos(videosData);
      }
      if (interviewsRes.ok) {
        const interviewsData = await interviewsRes.json();
        setInterviews(interviewsData);
      }
      if (chartsRes.ok) {
        const chartsData = await chartsRes.json();
        // Find this artist in the charts and get their badges
        const artistEntry = chartsData.find(entry => entry.artist_id === artistId);
        if (artistEntry && artistEntry.chart_badges) {
          setChartBadges(artistEntry.chart_badges);
        }
      }
    } catch (error) {
      console.error('Failed to fetch artist data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to follow artists');
      return;
    }

    try {
      const response = await fetch(`${API}/artists/${artistId}/follow`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setIsFollowing(data.following);
        toast.success(data.following ? 'Now following!' : 'Unfollowed');
        fetchArtistData();
      }
    } catch (error) {
      toast.error('Failed to follow artist');
    }
  };

  const handleRequest = async (trackId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to request tracks');
      return;
    }

    try {
      const response = await fetch(`${API}/requests/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ track_id: trackId })
      });

      const result = await safeJson(response);
      if (result.ok) {
        toast.success('Track requested! This helps the artist climb the charts.');
        fetchArtistData();
      } else {
        toast.error(result.error || 'Failed to request track');
      }
    } catch (error) {
      toast.error('Failed to request track');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold mb-2">Artist Not Found</h1>
          <p className="text-muted-foreground mb-4">This artist does not exist or has been removed.</p>
          <Link to="/artists">
            <Button>Browse Artists</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" data-testid="artist-page">
      {/* Full Page Background (Elite+ feature) */}
      {artist.background_url && (
        <>
          <div 
            className="fixed inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: `url(${artist.background_url.startsWith('/api') ? `${API.replace('/api', '')}${artist.background_url}` : artist.background_url})`,
              zIndex: 0
            }}
          />
          <div className="fixed inset-0 bg-gradient-to-b from-black/70 via-black/80 to-background" style={{ zIndex: 1 }} />
          {/* Animated overlay for Executive/Enterprise */}
          {artist.animated_overlay_enabled && (
            <div 
              className="fixed inset-0 opacity-30 animate-gradient-shift"
              style={{ 
                background: 'linear-gradient(45deg, rgba(109, 40, 217, 0.3), rgba(249, 115, 22, 0.3), rgba(109, 40, 217, 0.3))',
                backgroundSize: '400% 400%',
                zIndex: 2
              }}
            />
          )}
        </>
      )}
      
      {/* Content wrapper */}
      <div className="relative" style={{ zIndex: 10 }}>
        {/* Banner */}
        <div className="relative h-64 sm:h-80">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: artist.banner_url 
                ? `url(${artist.banner_url.startsWith('/api') ? `${API.replace('/api', '')}${artist.banner_url}` : artist.banner_url})` 
                : artist.background_url 
                  ? 'none'
                  : 'linear-gradient(135deg, hsl(266, 67%, 27%) 0%, hsl(19, 98%, 44%) 100%)' 
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        </div>

      {/* Artist Info */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10">
        <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-end mb-8">
          {/* Avatar */}
          <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-lg overflow-hidden border-4 border-background shadow-2xl flex-shrink-0">
            {artist.avatar_url ? (
              <img 
                src={artist.avatar_url.startsWith('/api') ? `${API.replace('/api', '')}${artist.avatar_url}` : artist.avatar_url} 
                alt={artist.stage_name} 
                className="w-full h-full object-cover" 
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <span className="text-4xl font-heading font-bold">{artist.stage_name?.[0] || '?'}</span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {/* Tier Badge */}
              <TierBadge 
                tier={artist.tier}
                subscriptionStatus={artist.subscription_status}
                isFounder={artist.is_founder}
                size="md"
                showLabel={true}
              />
              {/* Verified DMV Badge */}
              {artist.is_verified_dmv && (
                <VerifiedDMVBadge size="md" showLabel={true} />
              )}
              {artist.weekly_rank && artist.weekly_rank <= 20 && (
                <Badge className="bg-secondary/20 text-secondary">
                  <Trophy className="w-3 h-3 mr-1" />
                  #{artist.weekly_rank} This Week
                </Badge>
              )}
              {/* Chart Badges */}
              {chartBadges.map((badge, index) => (
                <Badge key={index} className="bg-gradient-to-r from-primary/20 to-secondary/20 text-foreground border border-primary/30">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  {badge}
                </Badge>
              ))}
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase mb-2" data-testid="artist-name">
              {artist.stage_name}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
              {artist.neighborhood && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {artist.neighborhood}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {artist.followers_count?.toLocaleString() || 0} followers
              </span>
              <span className="flex items-center gap-1">
                <Play className="w-4 h-4" />
                {artist.total_plays?.toLocaleString() || 0} plays
              </span>
            </div>

            {/* Genres */}
            {artist.genres?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {artist.genres.map((genre) => (
                  <Badge key={genre} variant="outline">{genre}</Badge>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={handleFollow}
                variant={isFollowing ? 'outline' : 'default'}
                className={isFollowing ? '' : 'bg-primary hover:bg-primary/90'}
                data-testid="follow-btn"
              >
                {isFollowing ? 'Following' : 'Follow'}
                <Plus className="ml-2 w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                className="border-border"
                onClick={async () => {
                  const shareUrl = window.location.href;
                  
                  // Try native share on mobile
                  if (navigator.share) {
                    try {
                      await navigator.share({
                        title: `${artist.name} on DMV Soundstage`,
                        text: `Check out ${artist.name} on DMV Soundstage!`,
                        url: shareUrl
                      });
                      return;
                    } catch (err) {
                      // User cancelled - that's fine
                      if (err.name === 'AbortError') return;
                      // Other error - fall through to clipboard
                    }
                  }
                  
                  // Fallback: copy to clipboard
                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success('Link copied to clipboard!');
                  } catch (e) {
                    // Legacy fallback for older browsers
                    const input = document.createElement('input');
                    input.value = shareUrl;
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    document.body.removeChild(input);
                    toast.success('Link copied to clipboard!');
                  }
                }}
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              
              {/* Social Links */}
              {artist.socials?.instagram && (
                <a 
                  href={artist.socials.instagram.startsWith('http') ? artist.socials.instagram : `https://${artist.socials.instagram}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button variant="ghost" size="icon"><Instagram className="w-5 h-5" /></Button>
                </a>
              )}
              {artist.socials?.twitter && (
                <a 
                  href={artist.socials.twitter.startsWith('http') ? artist.socials.twitter : `https://${artist.socials.twitter}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button variant="ghost" size="icon"><Twitter className="w-5 h-5" /></Button>
                </a>
              )}
              {artist.socials?.website && (
                <a 
                  href={artist.socials.website.startsWith('http') ? artist.socials.website : `https://${artist.socials.website}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button variant="ghost" size="icon"><Globe className="w-5 h-5" /></Button>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Bio */}
        {artist.bio && (
          <Card className="glass-card border-border p-6 mb-8">
            <p className="text-muted-foreground">{artist.bio}</p>
          </Card>
        )}

        {/* Top Supporters Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2">
            {/* Creator's Top 20 Chart */}
            <CreatorChartCard 
              artistHandle={artist.handle || artistId} 
              artistId={artistId}
              limit={5}
              showViewAll={true}
            />
            {/* My Fan Status (if logged in) */}
            {isAuthenticated && (
              <div className="mt-6">
                <MyFanStatus artistId={artistId} getAuthHeaders={getAuthHeaders} />
              </div>
            )}
          </div>
          <div>
            <TopFansCard artistId={artistId} />
          </div>
        </div>

        {/* Fan Subscriptions Section */}
        <div className="mb-8">
          <ArtistSubscriptionSection 
            artistId={artistId} 
            artistName={artist.stage_name}
          />
        </div>

        {/* Content Tabs */}
        <Tabs defaultValue="tracks" className="mb-12">
          <TabsList className="bg-muted/50 mb-6">
            <TabsTrigger value="tracks" data-testid="tracks-tab">
              <Music className="w-4 h-4 mr-2" />
              Tracks ({tracks.length})
            </TabsTrigger>
            <TabsTrigger value="videos" data-testid="videos-tab">
              <Video className="w-4 h-4 mr-2" />
              Videos ({videos.length})
            </TabsTrigger>
            <TabsTrigger value="posts" data-testid="posts-tab">
              <MessageCircle className="w-4 h-4 mr-2" />
              Posts
            </TabsTrigger>
            {interviews.length > 0 && (
              <TabsTrigger value="interviews" data-testid="interviews-tab">
                <Mic2 className="w-4 h-4 mr-2" />
                Interviews ({interviews.length})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="tracks">
            <div className="space-y-2">
              {tracks.length > 0 ? (
                tracks.map((track, index) => (
                  <motion.div
                    key={track.track_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="track-row flex items-center gap-4 p-3 rounded-lg group"
                  >
                    <span className="w-6 text-center text-sm text-muted-foreground">{index + 1}</span>
                    <div 
                      className="w-12 h-12 bg-muted rounded overflow-hidden relative flex-shrink-0 cursor-pointer"
                      onClick={() => {
                        // If DSP track with embed URL, open in new tab
                        if (track.embed_url) {
                          window.open(track.spotify_url || track.dsp_url || track.embed_url, '_blank');
                        } else if (track.audio_url) {
                          playTrack(track, tracks);
                        }
                      }}
                    >
                      {track.cover_url ? (
                        <img src={track.cover_url} alt={track.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <Music className="w-5 h-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="play-icon absolute inset-0 bg-background/50 flex items-center justify-center">
                        <Play className="w-5 h-5 text-white" fill="white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{track.title}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {track.genre && <Badge variant="outline" className="text-xs">{track.genre}</Badge>}
                        <span>{track.plays?.toLocaleString() || 0} plays</span>
                        {track.spotify_url && <Badge variant="outline" className="text-xs text-green-500">Spotify</Badge>}
                      </div>
                    </div>
                    {/* Show external link for DSP tracks */}
                    {(track.spotify_url || track.dsp_url) && (
                      <a 
                        href={track.spotify_url || track.dsp_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Globe className="w-4 h-4" />
                      </a>
                    )}
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleRequest(track.track_id)}
                      data-testid={`request-track-${track.track_id}`}
                    >
                      Request
                    </Button>
                  </motion.div>
                ))
              ) : (
                <StageEmptyState variant="tracks" size="sm" />
              )}
            </div>
          </TabsContent>

          <TabsContent value="videos">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.length > 0 ? (
                videos.map((video) => {
                  const videoUrl = video.video_url || video.youtube_url || video.external_url;
                  return (
                  <a 
                    key={video.video_id}
                    href={videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`video-card-${video.video_id}`}
                  >
                  <Card className="glass-card border-border overflow-hidden hover-lift cursor-pointer">
                    <div className="aspect-video bg-muted relative">
                      {video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <Video className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-background/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <Play className="w-12 h-12 text-white" fill="white" />
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="font-medium truncate">{video.title}</p>
                      <p className="text-sm text-muted-foreground">{video.plays?.toLocaleString() || 0} views</p>
                    </div>
                  </Card>
                  </a>
                  );
                })
              ) : (
                <div className="col-span-full">
                  <StageEmptyState variant="videos" size="sm" />
                </div>
              )}
            </div>
          </TabsContent>

          {/* Posts Tab */}
          <TabsContent value="posts">
            <ArtistPostsFeed artistId={artistId} />
          </TabsContent>

          {/* Interviews Tab */}
          {interviews.length > 0 && (
            <TabsContent value="interviews">
              <div className="space-y-4">
                {interviews.map((interview) => (
                  <Link key={interview.interview_id} to={`/interviews/${interview.interview_id}`}>
                    <Card className="glass-card border-border hover:border-border transition-all p-4">
                      <div className="flex gap-4">
                        <div className="w-32 h-20 rounded bg-muted/30 flex items-center justify-center flex-shrink-0">
                          {interview.cover_url ? (
                            <img src={interview.cover_url} alt="" className="w-full h-full object-cover rounded" />
                          ) : (
                            <Mic2 className="w-8 h-8 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold line-clamp-1">{interview.title}</h3>
                          <p className="text-sm text-muted-foreground">Host: {interview.host_name}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" /> {interview.plays || 0}
                            </span>
                            {interview.duration && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {interview.duration}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
      </div>{/* End content wrapper */}
    </div>
  );
}
